import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Mail, User, Phone, Lock, Eye, EyeOff, Loader2, AlertCircle, CheckCircle2, Camera, Upload, RefreshCw, ArrowLeft, ShieldCheck } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext'; // 👈 Theme Support Added

// view: 'login' | 'signup' | 'signup-otp' | 'forgot-password' | 'forgot-otp'

const AuthModal = ({ isOpen, onClose }) => {
  const { login } = useAuth();
  const { isDark } = useTheme(); // 👈 Access Theme State
  const navigate = useNavigate();
  
  const [view, setView] = useState('login');
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  // Login + Signup form data
  const [formData, setFormData] = useState({ email: '', username: '', phone: '', password: '' });
  
  // OTP input (shared across signup-otp and forgot-otp)
  const [otp, setOtp] = useState('');
  // Email held during multi-step flows
  const [pendingEmail, setPendingEmail] = useState('');

  // Forgot password flow
  const [forgotEmail, setForgotEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');

  // Profile picture
  const [profilePicture, setProfilePicture] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [isCameraActive, setIsCameraActive] = useState(false);

  // Timer states
  const [resendTimer, setResendTimer] = useState(0);
  const [canResend, setCanResend] = useState(false);

  // Holds signup payload for OTP resend without re-uploading picture
  const pendingSignupRef = useRef(null);

  // Camera refs
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const fileInputRef = useRef(null);

  // Timer countdown effect
  useEffect(() => {
    let interval;
    if (resendTimer > 0) {
      interval = setInterval(() => {
        setResendTimer((prev) => prev - 1);
      }, 1000);
    } else if (resendTimer === 0 && (view === 'signup-otp' || view === 'forgot-otp')) {
      setCanResend(true);
    }
    return () => clearInterval(interval);
  }, [resendTimer, view]);

  // Format seconds into MM:SS
  const formatTime = (seconds) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s < 10 ? '0' : ''}${s}`;
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    setIsCameraActive(false);
  };

  const resetAll = () => {
    setFormData({ email: '', username: '', phone: '', password: '' });
    setProfilePicture(null);
    setPreviewUrl(null);
    setIsCameraActive(false);
    setErrorMsg('');
    setSuccessMsg('');
    setOtp('');
    setPendingEmail('');
    setForgotEmail('');
    setNewPassword('');
    setConfirmNewPassword('');
    setResendTimer(0);
    setCanResend(false);
    pendingSignupRef.current = null;
  };

  useEffect(() => {
    if (!isOpen) {
      stopCamera();
      resetAll();
      setView('login');
    }
    return () => stopCamera();
  }, [isOpen]);

  if (!isOpen) return null;

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setErrorMsg('');
  };

  const startCamera = async () => {
    setErrorMsg('');
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { facingMode: 'user', width: 400, height: 400 } 
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
      setIsCameraActive(true);
      setPreviewUrl(null);
    } catch (err) {
      console.error("Camera access denied:", err);
      setErrorMsg("Camera access denied. Please upload a file instead.");
    }
  };

  const capturePhoto = () => {
    if (videoRef.current && canvasRef.current) {
      const context = canvasRef.current.getContext('2d');
      canvasRef.current.width = videoRef.current.videoWidth;
      canvasRef.current.height = videoRef.current.videoHeight;
      
      context.drawImage(videoRef.current, 0, 0, canvasRef.current.width, canvasRef.current.height);
      
      canvasRef.current.toBlob((blob) => {
        if (blob) {
          const file = new File([blob], `live_capture_${Date.now()}.jpg`, { type: "image/jpeg" });
          setProfilePicture(file);
          setPreviewUrl(URL.createObjectURL(blob));
          stopCamera();
        }
      }, 'image/jpeg', 0.9);
    }
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setProfilePicture(file);
      setPreviewUrl(URL.createObjectURL(file));
      stopCamera(); 
    }
  };

  const clearPhoto = () => {
    setProfilePicture(null);
    setPreviewUrl(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const parseBackendError = (detail, defaultMessage) => {
    if (!detail) return defaultMessage;
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail)) {
      return detail.map(err => `Error in ${err.loc?.[1] || 'field'}: ${err.msg}`).join(' | ');
    }
    return defaultMessage;
  };

  const buildSignupFormData = () => {
    const saved = pendingSignupRef.current;
    const fd = new FormData();
    fd.append('email', saved.email);
    fd.append('name', saved.name);
    fd.append('password', saved.password);
    fd.append('mobile_number', saved.mobile_number);
    if (profilePicture) fd.append('profile_picture', profilePicture);
    return fd;
  };

  // ── Login ──────────────────────────────────────────────────────────────────
  const handleLoginSubmit = async () => {
    const payload = new URLSearchParams();
    payload.append('username', formData.email.trim());
    payload.append('password', formData.password);

    const response = await fetch('/api/login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: payload
    });
    const data = await response.json();

    if (response.ok) {
      setSuccessMsg('Login successful!');
      if (data.access_token) {
        const safeUser = {
          email: data.user.email,
          name: data.user.name || data.user.email.split('@')[0],
          mobile_number: data.user.mobile_number,
          profile_picture: data.user.profile_picture
            ? `http://127.0.0.1:8000${data.user.profile_picture}`
            : null
        };
        login(data.access_token, safeUser);
        navigate('/profile');
        if (onClose) onClose();
      }
    } else {
      setErrorMsg(parseBackendError(data.detail, 'Invalid email or password.'));
    }
  };

  // ── Signup step 1 — send OTP ───────────────────────────────────────────────
  const handleSendSignupOTP = async () => {
    const phone = formData.phone.trim();
    if (!/^[6-9]\d{9}$/.test(phone)) {
      setErrorMsg('Please enter a valid 10-digit mobile number.');
      return;
    }

    pendingSignupRef.current = {
      email: formData.email.trim(),
      name: formData.username.trim(),
      password: formData.password,
      mobile_number: phone
    };

    const response = await fetch('/api/send-signup-otp', {
      method: 'POST',
      body: buildSignupFormData()
    });
    const data = await response.json();

    if (response.ok) {
      setPendingEmail(formData.email.trim());
      setOtp('');
      setSuccessMsg(`OTP sent to ${formData.email.trim()}`);
      
      // Start Timer
      setResendTimer(120);
      setCanResend(false);
      
      setView('signup-otp');
    } else {
      setErrorMsg(parseBackendError(data.detail, 'Failed to send OTP.'));
    }
  };

  // ── Signup step 2 — verify OTP ────────────────────────────────────────────
  const handleVerifySignupOTP = async () => {
    const response = await fetch('/api/verify-signup-otp', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: pendingEmail, otp: otp.trim() })
    });
    const data = await response.json();

    if (response.ok) {
      setSuccessMsg('Account created! Logging you in…');
      const autoUserPayload = {
        email: pendingEmail,
        name: pendingSignupRef.current?.name || '',
        mobile_number: pendingSignupRef.current?.mobile_number || '',
        profile_picture: null
      };
      login(data.user_id, autoUserPayload);
      setTimeout(() => {
        navigate('/profile');
        if (onClose) onClose();
      }, 1000);
    } else {
      setErrorMsg(parseBackendError(data.detail, 'OTP verification failed.'));
    }
  };

  // ── Resend signup OTP ─────────────────────────────────────────────────────
  const handleResendSignupOTP = async () => {
    setIsLoading(true);
    setErrorMsg('');
    setSuccessMsg('');
    try {
      const response = await fetch('/api/send-signup-otp', {
        method: 'POST',
        body: buildSignupFormData()
      });
      const data = await response.json();
      if (response.ok) {
        setOtp('');
        setSuccessMsg('OTP resent!');
        // Reset Timer
        setResendTimer(120);
        setCanResend(false);
      } else {
        setErrorMsg(parseBackendError(data.detail, 'Failed to resend OTP.'));
      }
    } catch {
      setErrorMsg('Connection error.');
    } finally {
      setIsLoading(false);
    }
  };

  // ── Forgot password — send OTP ────────────────────────────────────────────
  const handleForgotPasswordSubmit = async () => {
    const response = await fetch('/api/forgot-password', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: forgotEmail.trim() })
    });
    const data = await response.json();

    if (response.ok) {
      setPendingEmail(forgotEmail.trim());
      setOtp('');
      setSuccessMsg(data.message);
      
      // Start Timer
      setResendTimer(120);
      setCanResend(false);
      
      setView('forgot-otp');
    } else {
      setErrorMsg(parseBackendError(data.detail, 'Failed to send reset OTP.'));
    }
  };

  // ── Forgot password — resend OTP ──────────────────────────────────────────
  const handleResendForgotOTP = async () => {
    setIsLoading(true);
    setErrorMsg('');
    setSuccessMsg('');
    try {
      const response = await fetch('/api/forgot-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: pendingEmail })
      });
      if (response.ok) {
        setOtp('');
        setSuccessMsg('OTP resent!');
        // Reset Timer
        setResendTimer(120);
        setCanResend(false);
      }
    } catch {
      setErrorMsg('Connection error.');
    } finally {
      setIsLoading(false);
    }
  };

  // ── Forgot password — reset ───────────────────────────────────────────────
  const handleResetPasswordSubmit = async () => {
    if (newPassword !== confirmNewPassword) {
      setErrorMsg('Passwords do not match.');
      return;
    }
    const response = await fetch('/api/reset-password', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: pendingEmail, otp: otp.trim(), new_password: newPassword })
    });
    const data = await response.json();

    if (response.ok) {
      setSuccessMsg('Password reset! Please log in with your new password.');
      setTimeout(() => {
        resetAll();
        setView('login');
      }, 1500);
    } else {
      setErrorMsg(parseBackendError(data.detail, 'Password reset failed.'));
    }
  };

  // ── Unified submit dispatcher ─────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setErrorMsg('');
    setSuccessMsg('');
    try {
      if (view === 'login')            await handleLoginSubmit();
      else if (view === 'signup')      await handleSendSignupOTP();
      else if (view === 'signup-otp')  await handleVerifySignupOTP();
      else if (view === 'forgot-password') await handleForgotPasswordSubmit();
      else if (view === 'forgot-otp')  await handleResetPasswordSubmit();
    } catch (error) {
      console.error('Fetch Error:', error);
      setErrorMsg('Cannot connect to server. Ensure FastAPI is active.');
    } finally {
      setIsLoading(false);
    }
  };

  // ── View metadata ─────────────────────────────────────────────────────────
  const viewMeta = {
    'login':           { title: 'Adventure Starts Here!', subtitle: 'Log in to view your travel dashboard.' },
    'signup':          { title: 'Join the Odessey',       subtitle: 'Sign up to build your custom space.' },
    'signup-otp':      { title: 'Verify Your Email',      subtitle: `Enter the 6-digit OTP sent to ${pendingEmail}` },
    'forgot-password': { title: 'Forgot Password?',       subtitle: "Enter your email and we'll send a reset OTP." },
    'forgot-otp':      { title: 'Reset Password',         subtitle: `Enter the OTP sent to ${pendingEmail} and your new password.` },
  };
  const { title, subtitle } = viewMeta[view] || viewMeta['login'];

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 99999, backgroundColor: 'rgba(17, 24, 39, 0.75)', backdropFilter: 'blur(8px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px', boxSizing: 'border-box' }}>
      <div style={{ backgroundColor: isDark ? '#1f2937' : 'white', borderRadius: '20px', padding: '24px 20px', width: '100%', maxWidth: '380px', position: 'relative', boxShadow: isDark ? '0 25px 50px rgba(0,0,0,0.6)' : '0 25px 50px rgba(0,0,0,0.3)', animation: 'slideUp 0.3s ease-out', maxHeight: '90vh', overflowY: 'auto', border: isDark ? '1px solid #374151' : 'none', boxSizing: 'border-box', transition: 'background-color 0.3s, border-color 0.3s' }} className="hide-scrollbar">

        {/* Close button */}
        {onClose && (
          <button type="button" onClick={onClose} style={{ position: 'absolute', top: '16px', right: '16px', background: isDark ? '#374151' : '#f3f4f6', border: 'none', borderRadius: '50%', width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', transition: 'background 0.2s', color: isDark ? 'white' : '#4b5563' }}>
            <X size={16} />
          </button>
        )}

        {/* Back button for multi-step views */}
        {(view === 'signup-otp' || view === 'forgot-password' || view === 'forgot-otp') && (
          <button type="button" onClick={() => {
            setErrorMsg(''); setSuccessMsg('');
            if (view === 'signup-otp') { setView('signup'); setResendTimer(0); }
            else if (view === 'forgot-otp') { setView('forgot-password'); setResendTimer(0); }
            else setView('login');
          }} style={{ position: 'absolute', top: '16px', left: '16px', background: isDark ? '#374151' : '#f3f4f6', border: 'none', borderRadius: '50%', width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: isDark ? 'white' : '#4b5563' }}>
            <ArrowLeft size={16} />
          </button>
        )}

        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '18px' }}>
          {(view === 'signup-otp' || view === 'forgot-otp') && (
            <div style={{ width: '48px', height: '48px', borderRadius: '50%', backgroundColor: isDark ? '#064e3b' : '#f0fdf4', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 10px' }}>
              <ShieldCheck size={24} color="#16a34a" />
            </div>
          )}
          <h2 style={{ fontSize: '20px', fontWeight: '800', color: isDark ? '#f9fafb' : '#111827', marginBottom: '4px', letterSpacing: '-0.5px' }}>{title}</h2>
          <p style={{ color: isDark ? '#9ca3af' : '#6b7280', fontSize: '12px', lineHeight: '1.4' }}>{subtitle}</p>
        </div>

        {/* Alerts */}
        {errorMsg && (
          <div style={{ backgroundColor: isDark ? '#450a0a' : '#fef2f2', border: isDark ? '1px solid #7c2d12' : '1px solid #fca5a5', color: isDark ? '#fca5a5' : '#dc2626', padding: '10px', borderRadius: '10px', marginBottom: '14px', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '8px', boxSizing: 'border-box' }}>
            <AlertCircle size={16} style={{ flexShrink: 0 }} />
            <span style={{ wordBreak: 'break-word' }}>{errorMsg}</span>
          </div>
        )}
        {successMsg && (
          <div style={{ backgroundColor: isDark ? '#064e3b' : '#f0fdf4', border: isDark ? '1px solid #065f46' : '1px solid #86efac', color: isDark ? '#a7f3d0' : '#16a34a', padding: '10px', borderRadius: '10px', marginBottom: '14px', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '8px', boxSizing: 'border-box' }}>
            <CheckCircle2 size={16} /> {successMsg}
          </div>
        )}

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>

          {/* ── LOGIN view ──────────────────────────────────────────── */}
          {view === 'login' && (
            <>
              <div style={{ position: 'relative' }}>
                <Mail size={16} color="#9ca3af" style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)' }} />
                <input type="email" name="email" placeholder="Email Address" value={formData.email} required onChange={handleInputChange} style={{ width: '100%', padding: '12px 14px 12px 42px', borderRadius: '10px', border: isDark ? '1px solid #4b5563' : '1px solid #e5e7eb', backgroundColor: isDark ? '#111827' : '#f9fafb', color: isDark ? 'white' : '#111827', boxSizing: 'border-box', outline: 'none', fontSize: '14px' }} />
              </div>
              <div style={{ position: 'relative' }}>
                <Lock size={16} color="#9ca3af" style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)' }} />
                <input type={showPassword ? 'text' : 'password'} name="password" placeholder="Password" minLength={8} value={formData.password} required onChange={handleInputChange} style={{ width: '100%', padding: '12px 42px 12px 42px', borderRadius: '10px', border: isDark ? '1px solid #4b5563' : '1px solid #e5e7eb', backgroundColor: isDark ? '#111827' : '#f9fafb', color: isDark ? 'white' : '#111827', boxSizing: 'border-box', outline: 'none', fontSize: '14px' }} />
                <button type="button" onClick={() => setShowPassword(!showPassword)} style={{ position: 'absolute', right: '14px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer' }}>
                  {showPassword ? <EyeOff size={16} color="#9ca3af" /> : <Eye size={16} color="#9ca3af" />}
                </button>
              </div>
              <div style={{ textAlign: 'right', marginTop: '-2px' }}>
                <button type="button" onClick={() => { setErrorMsg(''); setSuccessMsg(''); setView('forgot-password'); }} style={{ background: 'none', border: 'none', color: '#16a34a', fontSize: '12px', cursor: 'pointer', fontWeight: '600' }}>
                  Forgot password?
                </button>
              </div>
              <button type="submit" disabled={isLoading} style={{ marginTop: '4px', backgroundColor: '#16a34a', color: 'white', padding: '12px', borderRadius: '10px', fontSize: '14px', fontWeight: '600', border: 'none', cursor: isLoading ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', boxShadow: '0 4px 6px rgba(22,163,74,0.2)' }}>
                {isLoading ? <Loader2 className="animate-spin" size={16} /> : 'Sign In Securely'}
              </button>
            </>
          )}

          {/* ── SIGNUP view ─────────────────────────────────────────── */}
          {view === 'signup' && (
            <>
              {/* Avatar picker */}
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: '2px' }}>
                <div style={{ position: 'relative', width: '76px', height: '76px', borderRadius: '50%', backgroundColor: isDark ? '#064e3b' : '#f0fdf4', border: isDark ? '2px dashed #059669' : '2px dashed #86efac', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', marginBottom: '8px' }}>
                  {previewUrl ? (
                    <img src={previewUrl} alt="Preview" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  ) : isCameraActive ? (
                    <video ref={videoRef} autoPlay playsInline muted style={{ width: '100%', height: '100%', objectFit: 'cover', transform: 'scaleX(-1)' }} />
                  ) : (
                    <User size={28} color="#16a34a" />
                  )}
                  <input type="file" ref={fileInputRef} accept="image/jpeg, image/png, image/webp" onChange={handleFileChange} style={{ display: 'none' }} />
                  <canvas ref={canvasRef} style={{ display: 'none' }} />
                </div>
                <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', justifyContent: 'center' }}>
                  {isCameraActive ? (
                    <button type="button" onClick={capturePhoto} style={{ backgroundColor: '#16a34a', color: 'white', border: 'none', padding: '5px 12px', borderRadius: '20px', fontSize: '11px', fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '5px' }}>
                      <Camera size={13} /> Capture
                    </button>
                  ) : previewUrl ? (
                    <button type="button" onClick={clearPhoto} style={{ backgroundColor: isDark ? '#374151' : '#f3f4f6', color: isDark ? '#d1d5db' : '#4b5563', border: 'none', padding: '5px 12px', borderRadius: '20px', fontSize: '11px', fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '5px' }}>
                      <RefreshCw size={13} /> Retake
                    </button>
                  ) : (
                    <>
                      <button type="button" onClick={() => fileInputRef.current.click()} style={{ backgroundColor: isDark ? '#374151' : '#f3f4f6', color: isDark ? '#d1d5db' : '#4b5563', border: 'none', padding: '5px 12px', borderRadius: '20px', fontSize: '11px', fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '5px' }}>
                        <Upload size={13} /> Upload
                      </button>
                      <button type="button" onClick={startCamera} style={{ backgroundColor: isDark ? '#374151' : '#f3f4f6', color: isDark ? '#d1d5db' : '#4b5563', border: 'none', padding: '5px 12px', borderRadius: '20px', fontSize: '11px', fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '5px' }}>
                        <Camera size={13} /> Camera
                      </button>
                    </>
                  )}
                </div>
              </div>

              <div style={{ position: 'relative' }}>
                <Mail size={16} color="#9ca3af" style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)' }} />
                <input type="email" name="email" placeholder="Email Address" value={formData.email} required onChange={handleInputChange} style={{ width: '100%', padding: '12px 14px 12px 42px', borderRadius: '10px', border: isDark ? '1px solid #4b5563' : '1px solid #e5e7eb', backgroundColor: isDark ? '#111827' : '#f9fafb', color: isDark ? 'white' : '#111827', boxSizing: 'border-box', outline: 'none', fontSize: '14px' }} />
              </div>
              <div style={{ position: 'relative' }}>
                <User size={16} color="#9ca3af" style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)' }} />
                <input type="text" name="username" placeholder="Full Name" value={formData.username} required onChange={handleInputChange} style={{ width: '100%', padding: '12px 14px 12px 42px', borderRadius: '10px', border: isDark ? '1px solid #4b5563' : '1px solid #e5e7eb', backgroundColor: isDark ? '#111827' : '#f9fafb', color: isDark ? 'white' : '#111827', boxSizing: 'border-box', outline: 'none', fontSize: '14px' }} />
              </div>
              <div style={{ position: 'relative' }}>
                <Phone size={16} color="#9ca3af" style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)' }} />
                <input type="tel" name="phone" placeholder="Mobile Number (10 digits)" value={formData.phone} required onChange={handleInputChange} style={{ width: '100%', padding: '12px 14px 12px 42px', borderRadius: '10px', border: isDark ? '1px solid #4b5563' : '1px solid #e5e7eb', backgroundColor: isDark ? '#111827' : '#f9fafb', color: isDark ? 'white' : '#111827', boxSizing: 'border-box', outline: 'none', fontSize: '14px' }} />
              </div>
              <div style={{ position: 'relative' }}>
                <Lock size={16} color="#9ca3af" style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)' }} />
                <input type={showPassword ? 'text' : 'password'} name="password" placeholder="Password (min 8 chars)" minLength={8} value={formData.password} required onChange={handleInputChange} style={{ width: '100%', padding: '12px 42px 12px 42px', borderRadius: '10px', border: isDark ? '1px solid #4b5563' : '1px solid #e5e7eb', backgroundColor: isDark ? '#111827' : '#f9fafb', color: isDark ? 'white' : '#111827', boxSizing: 'border-box', outline: 'none', fontSize: '14px' }} />
                <button type="button" onClick={() => setShowPassword(!showPassword)} style={{ position: 'absolute', right: '14px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer' }}>
                  {showPassword ? <EyeOff size={16} color="#9ca3af" /> : <Eye size={16} color="#9ca3af" />}
                </button>
              </div>
              <button type="submit" disabled={isLoading} style={{ marginTop: '4px', backgroundColor: '#16a34a', color: 'white', padding: '12px', borderRadius: '10px', fontSize: '14px', fontWeight: '600', border: 'none', cursor: isLoading ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', boxShadow: '0 4px 6px rgba(22,163,74,0.2)' }}>
                {isLoading ? <Loader2 className="animate-spin" size={16} /> : 'Continue — Send OTP'}
              </button>
            </>
          )}

          {/* ── SIGNUP-OTP view ─────────────────────────────────────── */}
          {view === 'signup-otp' && (
            <>
              <input
                type="text"
                inputMode="numeric"
                pattern="\d{6}"
                maxLength={6}
                placeholder="Enter 6-digit OTP"
                value={otp}
                required
                onChange={e => { setOtp(e.target.value.replace(/\D/g, '')); setErrorMsg(''); }}
                style={{ width: '100%', padding: '12px', borderRadius: '10px', border: isDark ? '1px solid #4b5563' : '1px solid #e5e7eb', backgroundColor: isDark ? '#111827' : '#f9fafb', color: isDark ? 'white' : '#111827', boxSizing: 'border-box', outline: 'none', fontSize: '20px', textAlign: 'center', letterSpacing: '6px', fontWeight: '700' }}
              />
              <button type="submit" disabled={isLoading || otp.length !== 6} style={{ backgroundColor: '#16a34a', color: 'white', padding: '12px', borderRadius: '10px', fontSize: '14px', fontWeight: '600', border: 'none', cursor: (isLoading || otp.length !== 6) ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', boxShadow: '0 4px 6px rgba(22,163,74,0.2)', opacity: otp.length !== 6 ? 0.7 : 1 }}>
                {isLoading ? <Loader2 className="animate-spin" size={16} /> : 'Verify & Create Account'}
              </button>

              <div style={{ height: '22px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                {canResend ? (
                  <button type="button" disabled={isLoading} onClick={handleResendSignupOTP} style={{ background: 'none', border: 'none', color: '#16a34a', fontSize: '12px', fontWeight: '600', cursor: 'pointer', textAlign: 'center' }}>
                    Didn't receive OTP? Resend
                  </button>
                ) : (
                  <p style={{ color: isDark ? '#9ca3af' : '#6b7280', fontSize: '12px', textAlign: 'center', margin: 0 }}>
                    Resend OTP in {formatTime(resendTimer)}
                  </p>
                )}
              </div>
            </>
          )}

          {/* ── FORGOT-PASSWORD view ────────────────────────────────── */}
          {view === 'forgot-password' && (
            <>
              <div style={{ position: 'relative' }}>
                <Mail size={16} color="#9ca3af" style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)' }} />
                <input type="email" placeholder="Your registered email" value={forgotEmail} required onChange={e => { setForgotEmail(e.target.value); setErrorMsg(''); }} style={{ width: '100%', padding: '12px 14px 12px 42px', borderRadius: '10px', border: isDark ? '1px solid #4b5563' : '1px solid #e5e7eb', backgroundColor: isDark ? '#111827' : '#f9fafb', color: isDark ? 'white' : '#111827', boxSizing: 'border-box', outline: 'none', fontSize: '14px' }} />
              </div>
              <button type="submit" disabled={isLoading} style={{ backgroundColor: '#16a34a', color: 'white', padding: '12px', borderRadius: '10px', fontSize: '14px', fontWeight: '600', border: 'none', cursor: isLoading ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', boxShadow: '0 4px 6px rgba(22,163,74,0.2)' }}>
                {isLoading ? <Loader2 className="animate-spin" size={16} /> : 'Send Reset OTP'}
              </button>
            </>
          )}

          {/* ── FORGOT-OTP view (OTP + new password) ───────────────── */}
          {view === 'forgot-otp' && (
            <>
              <input
                type="text"
                inputMode="numeric"
                pattern="\d{6}"
                maxLength={6}
                placeholder="Enter 6-digit OTP"
                value={otp}
                required
                onChange={e => { setOtp(e.target.value.replace(/\D/g, '')); setErrorMsg(''); }}
                style={{ width: '100%', padding: '12px', borderRadius: '10px', border: isDark ? '1px solid #4b5563' : '1px solid #e5e7eb', backgroundColor: isDark ? '#111827' : '#f9fafb', color: isDark ? 'white' : '#111827', boxSizing: 'border-box', outline: 'none', fontSize: '20px', textAlign: 'center', letterSpacing: '6px', fontWeight: '700' }}
              />
              <div style={{ position: 'relative' }}>
                <Lock size={16} color="#9ca3af" style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)' }} />
                <input type={showNewPassword ? 'text' : 'password'} placeholder="New password (min 8 chars)" minLength={8} value={newPassword} required onChange={e => { setNewPassword(e.target.value); setErrorMsg(''); }} style={{ width: '100%', padding: '12px 42px 12px 42px', borderRadius: '10px', border: isDark ? '1px solid #4b5563' : '1px solid #e5e7eb', backgroundColor: isDark ? '#111827' : '#f9fafb', color: isDark ? 'white' : '#111827', boxSizing: 'border-box', outline: 'none', fontSize: '14px' }} />
                <button type="button" onClick={() => setShowNewPassword(!showNewPassword)} style={{ position: 'absolute', right: '14px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer' }}>
                  {showNewPassword ? <EyeOff size={16} color="#9ca3af" /> : <Eye size={16} color="#9ca3af" />}
                </button>
              </div>
              <div style={{ position: 'relative' }}>
                <Lock size={16} color="#9ca3af" style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)' }} />
                <input type="password" placeholder="Confirm new password" minLength={8} value={confirmNewPassword} required onChange={e => { setConfirmNewPassword(e.target.value); setErrorMsg(''); }} style={{ width: '100%', padding: '12px 14px 12px 42px', borderRadius: '10px', border: isDark ? '1px solid #4b5563' : '1px solid #e5e7eb', backgroundColor: isDark ? '#111827' : '#f9fafb', color: isDark ? 'white' : '#111827', boxSizing: 'border-box', outline: 'none', fontSize: '14px' }} />
              </div>
              <button type="submit" disabled={isLoading || otp.length !== 6} style={{ backgroundColor: '#16a34a', color: 'white', padding: '12px', borderRadius: '10px', fontSize: '14px', fontWeight: '600', border: 'none', cursor: (isLoading || otp.length !== 6) ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', boxShadow: '0 4px 6px rgba(22,163,74,0.2)', opacity: otp.length !== 6 ? 0.7 : 1 }}>
                {isLoading ? <Loader2 className="animate-spin" size={16} /> : 'Reset Password'}
              </button>

              <div style={{ height: '22px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                {canResend ? (
                  <button type="button" disabled={isLoading} onClick={handleResendForgotOTP} style={{ background: 'none', border: 'none', color: '#16a34a', fontSize: '12px', fontWeight: '600', cursor: 'pointer', textAlign: 'center' }}>
                    Try again. Resend OTP.
                  </button>
                ) : (
                  <p style={{ color: isDark ? '#9ca3af' : '#6b7280', fontSize: '12px', textAlign: 'center', margin: 0 }}>
                    Resend OTP in {formatTime(resendTimer)}
                  </p>
                )}
              </div>
            </>
          )}

        </form>

        {/* Footer: toggle between login and signup */}
        {(view === 'login' || view === 'signup') && (
          <p style={{ textAlign: 'center', marginTop: '16px', fontSize: '13px', color: isDark ? '#9ca3af' : '#6b7280' }}>
            {view === 'login' ? "Don't have an account?" : 'Already have an account?'}
            <button type="button" onClick={() => { setErrorMsg(''); setSuccessMsg(''); setView(view === 'login' ? 'signup' : 'login'); resetAll(); }} style={{ background: 'none', border: 'none', color: '#16a34a', fontWeight: '700', cursor: 'pointer', marginLeft: '6px', fontSize: '13px' }}>
              {view === 'login' ? 'Sign Up' : 'Log In'}
            </button>
          </p>
        )}

        <style>{`
          @keyframes slideUp { from { opacity: 0; transform: translateY(15px) scale(0.97); } to { opacity: 1; transform: translateY(0) scale(1); } }
          .animate-spin { animation: spin 1s linear infinite; }
          @keyframes spin { 100% { transform: rotate(360deg); } }
          .hide-scrollbar::-webkit-scrollbar { display: none; }
          .hide-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
          input:focus {
            border-color: #16a34a !important;
            box-shadow: 0 0 0 3px rgba(22, 163, 74, 0.15) !important;
          }
        `}</style>
      </div>
    </div>
  );
};

export default AuthModal;